import {
  LoanCaseForOfferDTO,
  PricingParamsForOfferDTO,
  ProductInstanceForOfferDTO,
} from "@smartx/mikro-vt-client-api"
import { useContext, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "@george-labs.com/design-system"
import { IField } from "../../components/DataList/DataList"
import { getAddress } from "../../functions/getAddress"
import { VALUE_LIST_ENUM } from "../../functions/setCodeTableValues"
import ConfigContext from "../../providers/ConfigProvider"

export const useOfferHelperUtil = () => {
  const configContext = useContext(ConfigContext)
  const { t } = useTranslation()

  const getInterestTypeField = (pricing: PricingParamsForOfferDTO): IField => {
    const field: IField = {
      label: t("offers.calculator.fields.interestType"),
      value: pricing.interestType,
      fieldType: "CVT",
      list: VALUE_LIST_ENUM.InterestBaseListBiancoParam,
      appendText:
        " + " +
        (pricing.interestType === "FIX"
          ? pricing.interestPerYearNet
          : pricing.loanInterestDiscountMarge) +
        t("common.percentage") +
        " " +
        t("common.per.year"),
    }

    if (["1HAVI_BUBOR", "3HAVI_BUBOR"].includes(pricing.interestType)) {
      field.info = (
        <>
          {t("case.fields.productInstance.loanInterestMerged.BUBOR.tooltip")}{" "}
          <Link
            href={configContext?.interestBuborUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {configContext?.interestBuborUrlText}
          </Link>
        </>
      )
    } else if (
      ["1HAVI_EURIBOR", "3HAVI_EURIBOR"].includes(pricing.interestType)
    ) {
      field.info = (
        <>
          {t("case.fields.productInstance.loanInterestMerged.EURIBOR.tooltip")}
        </>
      )
    }

    return field
  }

  const getPricingFields = useMemo(() => {
    return (offerProduct: ProductInstanceForOfferDTO) => {
      if (!offerProduct?.pricingParamsForOfferDTO) {
        return []
      }

      const pricing = offerProduct.pricingParamsForOfferDTO
      const durationMaxInMonth = pricing.durationMax
      const durationMaxInYears = durationMaxInMonth / 12

      const fieldArr: Array<IField> = [
        offerProduct?.loanAmount >= 0
          ? {
              label: t("offers.calculator.fields.loanAmount"),
              value: offerProduct.loanAmount,
              fieldType: "number",
            }
          : null,
        {
          label: t("offers.calculator.fields.durationMax"),
          value: `${durationMaxInMonth} ${t("common.month")}`,
          appendText: `(${durationMaxInYears} ${t("common.year")})`,
          fieldType: "text",
        },
        getInterestTypeField(pricing),
        {
          label: t("offers.calculator.fields.contractFee"),
          value: pricing.contractFee,
          appendText: t("common.percentage"),
          fieldType: "decimal",
        },
        {
          label: t("offers.calculator.fields.availabilityFeePercent"),
          value: pricing.availabilityFeePercent,
          appendText: t("common.percentage"),
          fieldType: "decimal",
        },
        {
          label: t("offers.calculator.fields.handlingFeePerYearNet"),
          value: pricing.handlingFeePerYearNet,
          appendText: t("common.percentage") + t("common.per.month"),
          fieldType: "decimal",
        },
        {
          label: t("offers.calculator.fields.annualRevisionFee"),
          fieldType: "text",
          value:
            pricing.annualRevisionFee >= 0
              ? [
                  t("offers.calculator.fields.annualRevisionFee.prefix"),
                  pricing.annualRevisionFee,
                  t("offers.calculator.fields.annualRevisionFee.postfix"),
                ].join("")
              : "",
        },
        {
          label: t("offers.calculator.fields.garantiqaPerYearNet"),
          fieldType: "decimal",
          appendText: t("offers.calculator.fields.garantiqaPerYearNet.postfix"),
          value: pricing.garantiqaPerYearNet,
        },
      ]

      return fieldArr?.filter((el) => !!el)
    }
  }, [t, configContext])

  const getCustomerFields = useMemo(() => {
    return (offerData: LoanCaseForOfferDTO) => {
      const customer = offerData?.customer

      const fieldArr: Array<IField> = [
        {
          label: t("offers.summary.fields.companyName"),
          value: customer?.companyName,
          fieldType: "text",
        },
        {
          label: t("offers.summary.fields.companyLongName"),
          value: customer?.companyLongName,
          fieldType: "text",
        },
        {
          label: t("offers.summary.fields.taxNo"),
          value: customer?.taxNo,
          fieldType: "text",
        },
        {
          label: t("offers.summary.fields.address"),
          value: customer ? getAddress([customer.address], "HEADQUARTERS") : "",
          fieldType: "text",
        },
        {
          label: t("offers.summary.fields.teaor"),
          value: customer?.teaor,
          fieldType: "CVT",
          list: VALUE_LIST_ENUM.TeaorCodeList,
          prependText: customer.teaor + " ",
        },
        {
          label: t("offers.summary.fields.registrationDate"),
          value: customer?.registrationDate,
          fieldType: "date",
        },
        {
          label: t("offers.summary.fields.incomeBaseYear"),
          value: offerData?.financialInformation?.incomeBaseYear,
          fieldType: "number",
        },
        {
          label: t("offers.summary.fields.signers"),
          value: offerData?.individuals
            ?.filter((el) => el.signer)
            ?.map((el) => el.name)
            ?.join(", "),
          fieldType: "text",
        },
        {
          label: t("offers.summary.fields.contactName"),
          value: offerData?.contactName,
          fieldType: "text",
        },
      ]

      return fieldArr
    }
  }, [t])

  return {
    getPricingFields,
    getCustomerFields,
  }
}
